// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';
import { classify, stateNameFromUSPS } from './utils/helpers';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';
import members from './data/members.json';

const chamber = meta.chamber.substring(0, 1).toLowerCase();

const voteUrl = `https://ix.cnn.io/data/pol-congress-roll-call/${meta.congress}/votes/${meta.year}/${chamber}${meta.votenumber}/data.json`;

// construct official roll call vote url based on chamber
let officialVoteUrl;
if (chamber === 'h') {
  // For House: concatenate meta.year and vote number.
  officialVoteUrl = `https://clerk.house.gov/Votes/${meta.year}${meta.votenumber}`;
} else if (chamber === 's') {
  // For Senate: pad the vote number to 5 digits and include meta.session.
  const paddedVoteNumber = meta.votenumber.toString().padStart(5, '0');
  officialVoteUrl = `https://www.senate.gov/legislative/LIS/roll_call_votes/vote${meta.congress}${meta.session}/vote_${meta.congress}_${meta.session}_${paddedVoteNumber}.htm`;
}

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now
  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);
      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }
  const { pymChild } = window;
  pymChild.sendHeight();
  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const config = {
  boxSize: chamber == 'h' ? 10 : 16,
  boxGutter: chamber == 'h' ? 2 : 3,
  groupGutter: chamber == 'h' ? 8 : 16,
  rows: 5,
  columns: 5,
};
config.groupWidth = config.columns * (config.boxSize + config.boxGutter) + config.groupGutter;
config.groupHeight = config.rows * (config.boxSize + config.boxGutter) + config.groupGutter;

const filters = {
  search: '',
  party: 'All',
  vote: 'All',
};

const convertVote = (origVote) => {
  const oVote = `${origVote}`.toLowerCase();
  switch (oVote) {
    case 'yea': return 'yes';
    case 'aye': return 'yes';
    case 'yes': return 'yes';
    case 'nay': return 'no';
    case 'no': return 'no';
    case 'not voting': return 'other';
    case 'present': return 'other';
    case 'abstain': return 'other';
    default: return origVote;
  }
};

const convertResult = (rawResult) => {
  const oResult = rawResult.toLowerCase().split()[0];
  const passWords = ['agreed', 'passed', 'confirmed'];
  const failWords = ['rejected', 'failed'];
  if (passWords.some(word => oResult.includes(word))) {
    return 'passed';
  }
  if (failWords.some(word => oResult.includes(word))) {
    return 'failed';
  }
  return oResult;
};

function gridPosition(index) {
  const groupNum = Math.floor(index / (config.columns * config.rows));
  const row = index % config.rows;
  const col = Math.floor(index / config.rows) % config.columns;
  const groupX = (col * config.boxSize) + (col - 1) * config.boxGutter;
  const groupY = (row * config.boxSize) + (row - 1) * config.boxGutter;
  const groupXoffset = groupNum % config.groupsPerRow * config.groupWidth;
  const groupYoffset = Math.floor(groupNum / config.groupsPerRow) * config.groupHeight;
  return {
    x: groupX + groupXoffset,
    y: groupY + groupYoffset,
  };
}

const drawWaffle = (selector, data, classifier) => {
  const container = d3.select(selector);
  config.groupCount = Math.ceil(data.length / (config.columns * config.rows));
  config.width = Math.min(window.innerWidth, 610);
  config.groupsPerRow = Math.floor(config.width / config.groupWidth);
  config.numRows = Math.ceil(config.groupCount / config.groupsPerRow);
  config.height = config.numRows * config.groupHeight;
  // change width to size necessary...
  if (config.groupCount < config.groupsPerRow) {
    config.width = config.groupCount * config.groupWidth;
  }
  const svg = container.append('svg')
    .attr('width', config.width)
    .attr('height', config.height)
    .attr('viewBox', `0 0 ${config.width} ${config.height - config.groupGutter}`);
  const grid = svg.append('g')
    .attr('transform', `translate(${[config.boxGutter, config.boxGutter]})`)
    .selectAll('circle')
    .data(data)
    .enter()
    .append('circle')
    .attr('cx', (d, i) => gridPosition(i).x + config.boxSize / 2)
    .attr('cy', (d, i) => gridPosition(i).y + config.boxSize / 2)
    .attr('r', config.boxSize / 2)
    .attr('class', d => (classify(d[classifier])));
};

const voteSort = ['yes', 'no', 'other'];
let vpVote = false;

let votes;
// Global variables to store vote grouping and result for redraws
let globalVoteData;
let globalVoteResult;

let memberData = {};

d3.json(voteUrl).then((data) => {
  const voteDate = data.date.split('T')[0];

  d3.json(`https://ix.cnn.io/data/pol-congress-roll-call/members-${voteDate}.json`)
    .then((memberJson) => {
      memberData = memberJson;
      formatData(data, memberData);
    })
    .catch((error) => {
      memberData = members;
      formatData(data, memberData);
    });
});

function formatData(data, members) {
  for (const v in data.votes) {
    const didVpVote = data.votes[v].indexOf('VP');
    // If VP voted, remove from votes list
    if (didVpVote >= 0) {
      vpVote = v;
      data.votes[v].splice(didVpVote, 1);
    }
    data.votes[v].map((m) => {
      m.vote = v;
      m.voteGroup = convertVote(v);
      m.party = ['D', 'R'].includes(m.party) ? m.party : 'I';
      m.last_name = members[m.id].last;
      m.full_name = members[m.id].full;
      m.district = `${members[m.id].dist}`;
      m.stateName = stateNameFromUSPS(m.state);
    });
  }
  votes = Object.values(data.votes).flat();
  votes.sort((a, b) => (a.party < b.party ? -1 : 1));
  console.log(votes);
  const membersByVote = d3.nest()
    .key(d => d.voteGroup)
    .entries(votes);
  membersByVote.sort(
    (a, b) => (voteSort.indexOf(a.key) < voteSort.indexOf(b.key) ? -1 : 1),
  );
  // Store global data for later redraws
  globalVoteData = membersByVote;
  globalVoteResult = convertResult(data.result);
  console.log(data);
  const voteTitle = d3.select('#footnote')
    .html(`Source: <a href="${officialVoteUrl}" target="_blank">${chamber == 'h' ? 'US House of Representatives' : 'US Senate'}</a>`);
  redrawAll();
}

function drawGrid(voteData, voteResult) {
  const container = d3.select('#graphic-grid')
    .attr('class', `chamber ${chamber}`);
  container.html('');

  // For each vote group (yes/no/other) ...
  voteData.forEach((v) => {
    // Find which party is largest in this group
    const partyCounts = d3.nest()
      .key(d => d.party)
      .rollup(arr => arr.length)
      .entries(v.values)
      .sort((a, b) => b.value - a.value);

    // The party with the most members in this group
    const majorityParty = partyCounts[0].key;

    // Sort so that majorityParty is first
    v.values.sort((a, b) => {
      if (a.party === b.party) return 0;
      return (a.party === majorityParty) ? -1 : 1;
    });

    // Now build the waffle chart with that sorted array
    const voteGroupContainer = container
      .append('div')
      .attr('id', classify(v.key))
      .attr('class', `vote-group ${classify(voteResult)}`);

    // Add the group header
    voteGroupContainer.append('h4').html(d => generateVoteHeader(v));

    // Draw waffle
    drawWaffle(`#${classify(v.key)}`, v.values, 'party');
  });

  // If VP voted
  if (vpVote) {
    container.append('p')
      .attr('class', 'vp-vote')
      .text('The Vice President cast the deciding vote');
  }
}


function generateVoteHeader(vData) {
  let headerHtml = '';
  headerHtml += `<span class="total">${vData.key} (${vData.values.length})</span>`;
  const votesByParty = d3.nest()
    .key(d => d.party)
    .entries(vData.values);
  // Show pills if there is more than one party represented, and if the total votes is greater than 25
  if ((vData.values.length > 25) && (votesByParty.length > 1)) {
    votesByParty.forEach((v) => {
      headerHtml += `<span class="pill ${classify(v.key)}">${v.values.length}</span>`;
    });
  }
  return headerHtml;
}

function renderTable() {
  d3.select('#table-controls').classed('hidden', false);
  updateTable();

  // Populate filters with party and vote options
  const partyFilters = d3.select('#party-filter-buttons')
    .attr('class', 'filter-button-group');
  const partyList = ['All', ...new Set(votes.map(v => v.party))];
  const partyButtons = partyFilters.selectAll('.filter-button')
    .data(partyList)
    .enter()
    .append('div')
    .attr('class', d => `party filter-button ${d} ${d === 'All' ? 'active' : ''}`)
    .attr('data-group', 'party')
    .attr('data-filter', d => d)
    .text(d => d);

  const voteFilters = d3.select('#vote-filter-buttons')
    .attr('class', 'filter-button-group');
  const possibleVotes = [...new Set(votes.map(v => v.voteGroup))].sort(
    (a, b) => (voteSort.indexOf(a) < voteSort.indexOf(b) ? -1 : 1),
  );
  const voteList = ['All', ...possibleVotes];
  const voteButtons = voteFilters.selectAll('.filter-button')
    .data(voteList)
    .enter()
    .append('div')
    .attr('class', d => `vote filter-button ${d} ${d === 'All' ? 'active' : ''}`)
    .attr('data-group', 'vote')
    .attr('data-filter', d => d)
    .text(d => d);

  // Attach individual click handlers for party buttons
  partyButtons.on('click', function (d) {
    // If already active, reset filter to 'All'
    if (d3.select(this).classed('active')) {
      filters.party = 'All';
    } else {
      filters.party = d;
    }
    // Styling based on the data (e.g., D, R, I)
    partyButtons.attr('class', d => `party filter-button ${d} ${d === filters.party ? 'active' : ''}`);
    updateTable();
  });

  // Attach individual click handlers for vote buttons
  voteButtons.on('click', function (d) {
    if (d3.select(this).classed('active')) {
      filters.vote = 'All';
    } else {
      filters.vote = d;
    }
    voteButtons.attr('class', d => `vote filter-button ${d} ${d === filters.vote ? 'active' : ''}`);
    updateTable();
  });
}


// global sort state
let currentSortField = 'last_name';
let currentSortOrder = 'asc';

function updateTable() {
  let rows = votes;
  const isMobile = window.innerWidth < 625;
  // filter rows
  rows = votes.filter(d => d.full_name.toLowerCase().includes(filters.search)
    || d.stateName.toLowerCase().includes(filters.search)
    || d.district.toLowerCase().includes(filters.search));
  if (filters.party !== 'All') rows = rows.filter(d => d.party === filters.party);
  if (filters.vote !== 'All') rows = rows.filter(d => d.voteGroup === filters.vote);
  // sort rows: if sorting by last_name, use first name as tie-breaker
  rows.sort((a, b) => {
    let cmp;
    if (currentSortField === 'last_name') {
      cmp = a.last_name.localeCompare(b.last_name);
      if (cmp === 0) {
        // extract first name from full_name as tie-breaker
        const firstA = a.full_name.split(' ')[0];
        const firstB = b.full_name.split(' ')[0];
        cmp = firstA.localeCompare(firstB);
      }
    } else {
      cmp = a[currentSortField].localeCompare(b[currentSortField]);
    }
    return currentSortOrder === 'asc' ? cmp : -cmp;
  });
  // build table with clickable header cells for Name & State
  const table = d3.select('#graphic-table')
    .html('')
    .attr('class', `chamber ${chamber}`);
  // header data: Name and State are sortable
  const headerData = [
    { label: 'Name', field: 'last_name' },
    { label: 'State', field: 'stateName' },
    { label: chamber === 'h' ? (isMobile ? 'Dist.' : 'District') : '', field: null },
    { label: 'Party', field: null },
    { label: 'Vote', field: null },
  ];
  const tableHead = table.append('thead').append('tr');
  headerData.forEach((h) => {
    let headerText = h.label;
    if (h.field) {
      // add sort indicator if this header is active
      if (h.field === currentSortField) {
        headerText += currentSortOrder === 'asc' ? ' ▼' : ' ▲';
      }
      tableHead.append('th')
        .text(headerText)
        .style('cursor', 'pointer')
        .on('click', () => {
          // if clicking the same field, toggle order; otherwise, set new field and ascending
          if (currentSortField === h.field) {
            currentSortOrder = currentSortOrder === 'asc' ? 'desc' : 'asc';
          } else {
            currentSortField = h.field;
            currentSortOrder = 'asc';
          }
          updateTable();
        });
    } else {
      tableHead.append('th').text(headerText);
    }
  });
  // render table body rows
  const tableBody = table.append('tbody');
  const row = tableBody.selectAll('tr')
    .data(rows)
    .enter()
    .append('tr');
  row.append('td').text(d => (isMobile ? d.last_name : d.full_name));
  row.append('td').text(d => (isMobile ? d.state : d.stateName));
  row.append('td').text(d => (chamber === 'h' ? `${d.district}` : ''));
  row.append('td')
    .text(d => d.party)
    .attr('class', d => d.party);
  row.append('td')
    .text((d) => {
      // If vote is yes or no, use that text
      if (d.voteGroup === 'yes' || d.voteGroup === 'no') return d.voteGroup;
      // On mobile, show key if present in table and abbreviate "present" and "not voting"
      if (isMobile) {
        const voteLower = d.vote.toLowerCase();

        if (voteLower === 'present') {
          document.getElementById('legend-p').style.display = 'inline';
        }
        if (voteLower === 'present') return 'P';

        if (voteLower === 'not voting') {
          document.getElementById('legend-nv').style.display = 'inline';
        }
        if (voteLower === 'not voting') return 'NV';
      }
      // Otherwise, use the full vote text
      return d.vote;
    })
    .attr('class', d => d.voteGroup);
}

const searchBox = d3.select('#search-box');
searchBox.on('input', function () {
  filters.search = this.value.toLowerCase();
  updateTable();
});

// Redraw waffle chart and table when window size changes (desktop/mobile switch)
function redrawAll() {
  // Ensure that globalVoteData and globalVoteResult are defined
  if (!globalVoteData || !globalVoteResult) return;
  const chartType = getTypeParam();

  if (chartType !== 'table') {
    drawGrid(globalVoteData, globalVoteResult);
  }
  if (chartType !== 'grid') {
    renderTable();
  }
  window.pymChild.sendHeight();
}
window.addEventListener('resize', redrawAll);


function getTypeParam() {
  const thisUrl = window.location.search;
  const queryParams = new URLSearchParams(thisUrl);
  return queryParams.get('type');
}
